import axios from "axios";

const SIGNUP = `${process.env.REACT_APP_BACKEND_URL}/users`;
const LOG_DATA = `${process.env.REACT_APP_BACKEND_URL}/dummy`;

const FIREBASE_CALL = `${process.env.REACT_APP_BACKEND_URL}/users/firebaseToken`;
const DOCUMENT_API = `${process.env.REACT_APP_BACKEND_URL}/documents`;
const STEPS_API = `/steps`;
const STEP_API = "/step/";
const GET_NOTIFICATIONS = `${process.env.REACT_APP_BACKEND_URL}/notifications`;
const READ_ALL_NOTIFICATIONS = `${process.env.REACT_APP_BACKEND_URL}/notifications/updateunreads`;
const UNREAD_NOTIFICATIONS = `${process.env.REACT_APP_BACKEND_URL}/notifications/unread`;
const COMMENTS_API = `/comments`;
const FEATURE_FLAG = `${process.env.REACT_APP_BACKEND_URL}/feature-flags`;
const LOG_USER_ACTION = `${process.env.REACT_APP_BACKEND_URL}/loguseraction`

const AI_ENHANCE = `${process.env.REACT_APP_BACKEND_URL}/ai/enhance`;
const XGEN_AUTHORING_BASE =
  `${process.env.REACT_APP_API_URL}/api/v1/authoring`;

const ASK_AI = `${XGEN_AUTHORING_BASE}/ask`;

const AI_RECONSTRUCT =
  `${XGEN_AUTHORING_BASE}/refine-with-ai`;

const AI_CONTROLLED_LANGUAGE = `${XGEN_AUTHORING_BASE}/controlled-language`;

const INLINE_ACTION_INSTRUCTIONS = {
  rewrite:
    "Rewrite the text while preserving the original meaning, technical accuracy, and intent.",
  simplify:
    "Simplify the text using plain language while preserving key facts and intent.",
  grammar:
    "Correct grammar, punctuation, and sentence structure while preserving meaning and tone.",
  draft:
    "Expand the text into a more complete draft with clear flow and practical detail.",
  generate:
    "Generate fresh procedure-focused content based on these key points.",
};

const composeInlinePrompt = (action, content) => {
  const instruction = INLINE_ACTION_INSTRUCTIONS[action];
  if (!instruction) return content;
  return `${instruction}\n\nInput text:\n${content}`;
};

const aiAxios = axios.create();

aiAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("app_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

aiAxios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 404) {
      error.userMessage = "Feature not deployed yet on current backend environment.";
    } else if (status === 422) {
      error.userMessage = "Invalid content format. Please review the description and try again.";
    } else if (status === 502) {
      error.userMessage = "AI service is not configured properly. Please contact your administrator.";
    }

    console.error("[AI API] reconstruct failed", {
      status,
      message: error?.message,
      details: error?.response?.data,
    });

    return Promise.reject(error);
  }
);

// Authoring API axios instance with auth + endpoint-specific error handling.
export const authoringAxios = axios.create();

authoringAxios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("app_token");
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = token;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

authoringAxios.interceptors.response.use(
  (response) => {
    console.log("[authoringAxios] response", {
      url: response?.config?.url,
      status: response?.status,
      data: response?.data,
    });
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const data = error?.response?.data || {};
    const code = data?.code || data?.detail?.code;

    console.error("[authoringAxios] error", {
      url: error?.config?.url,
      status,
      code,
      data,
      message: error?.message,
    });

    if (status === 404) {
      error.userMessage = "Feature not deployed yet on current backend environment.";
    } else if (status === 422) {
      if (code === "TRANSFORMATION_INTEGRITY_VIOLATION") {
        error.userMessage =
          "The selected content changed during processing (e.g. an image was moved or removed). Please try again.";
      } else if (code === "APPROVED_SOURCE_EVIDENCE_NOT_FOUND") {
        error.userMessage =
          "No relevant approved or ingested source was found for this draft.";
      } else if (code === "EXPANSION_TOO_LONG") {
        error.userMessage =
          "The expansion exceeds the allowed length. Try selecting less text or a lower detail level.";
      } else {
        error.userMessage =
          data?.msg ||
          data?.detail?.message ||
          data?.message ||
          "Invalid content format. Please review the description and try again.";
      }
    } else if (status === 502) {
      error.userMessage =
        "AI service is not configured properly. Please contact your administrator.";
    } else if (status === 401 || status === 403) {
      error.userMessage =
        "Session expired or unauthorized request. Please sign in again.";
    }

    console.error("[Authoring API] request failed", {
      status,
      code,
      message: error?.message,
      details: data,
    });

    return Promise.reject(error);
  }
);

export function logUserAction(data) {
  return axios.post(LOG_USER_ACTION, data);
}

export function unreadNotification() {
  return axios.get(UNREAD_NOTIFICATIONS);
}

export async function getFeatureFlag(data){
  return axios.get(FEATURE_FLAG);
}

export function markNotificationRead(url) {
  return axios.post(GET_NOTIFICATIONS + url);
}

export function authSignup(data) {
  return axios.post(SIGNUP, data);
}

export function getNotifications() {
  return axios.get(GET_NOTIFICATIONS);
}

export function logData(data) {
  return axios.post(LOG_DATA, data);
}

export function sendFirebaseToken(data) {
  return axios.post(FIREBASE_CALL, data);
}

export function saveNewDocument(document) {
  return axios.put(DOCUMENT_API, document);
}

export function getAllDocument() {
  return axios.get(DOCUMENT_API);
}

export function deleteDocument(id, deletePermanently) {
  if (deletePermanently) {
    return axios.delete(DOCUMENT_API + "/" + id + "?deletePermanently=true");
  }
  return axios.delete(DOCUMENT_API + "/" + id);
}

export function saveNewStep(data, id) {
  return axios.put(DOCUMENT_API + "/" + id + STEPS_API, data);
}

export function deleteSteps(documentId, stepId) {
  return axios.delete(
    DOCUMENT_API + "/" + documentId + STEPS_API + "/" + stepId
  );
}

export function getDocumentById(documentId) {
  return axios.get(DOCUMENT_API + "/" + documentId);
}

export function getStepsByDocumentId(documentId) {
  return axios.get(DOCUMENT_API + "/" + documentId + STEPS_API);
}
export function getStepsByStepId(stepId) {
  return axios.get(DOCUMENT_API + STEP_API + stepId);
}

export function saveEditedStep(documentId, data, stepId) {
  return axios.patch(
    DOCUMENT_API + "/" + documentId + STEPS_API + "/" + stepId,
    data
  );
}

export function updateDocument(data, documentId) {
  return axios.patch(DOCUMENT_API + "/" + documentId, data);
}

export function getFeedbackdocument(documentId) {
  return axios.get(DOCUMENT_API + "/" + documentId + COMMENTS_API);
}

export function updateFeedbackStatus(documentId, stepId, feedbackId, data) {
  return axios.patch(
    DOCUMENT_API +
      "/" +
      documentId +
      "/steps/" +
      stepId +
      "/comments/" +
      feedbackId,
    data
  );
}

export function getStepFeedbackData(documentId, stepId) {
  return axios.get(DOCUMENT_API + "/" + documentId + "/steps/" + stepId);
}

export function readAllNotifications(value) {
  return axios.post(READ_ALL_NOTIFICATIONS);
}

export function uploadMedia(file) {
  return axios.post(DOCUMENT_API + "/files", file);
}

export function restoreDocument(id) {
  return axios.post(DOCUMENT_API + "/restore" + "/" + id);
}

export function reconstructWithAI(payload) {
  return aiAxios.post(AI_RECONSTRUCT, payload, {
    headers: {
      "Content-Type": "application/json",
    },
    params: { rows: 10 },
    timeout: 30000,
  });
}

export function enhanceTextWithAI(payload) {
  return axios
    .post(AI_ENHANCE, payload, {
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 30000,
    })


    .catch((error) => {
      // Backward compatibility for environments where /ai/enhance is not deployed yet.
      if (error?.response?.status !== 404) {
        throw error;
      }



      const action = String(payload?.action || "rewrite").toLowerCase().trim();
      const category = payload?.category || "Setup Instruction";
      const content = String(payload?.content || "").trim();

      if (!content) {
        throw error;
      }

      const endpoint =
        action === "rewrite"
          ? `${XGEN_AUTHORING_BASE}/rewrite?rows=10`
          : action === "simplify"
          ? `${XGEN_AUTHORING_BASE}/simplify?rows=10`
          : action === "grammar"
          ? `${XGEN_AUTHORING_BASE}/grammar?rows=10`
          : action === "draft"
          ? `${XGEN_AUTHORING_BASE}/draft?rows=10`
          : action === "generate"
          ? `${XGEN_AUTHORING_BASE}/generate?operation=generate&rows=10`
          : `${XGEN_AUTHORING_BASE}/refine-with-ai?rows=10`;

      const fallbackContent =
        action === "rewrite" || action === "simplify" || action === "grammar"
          ? content
          : composeInlinePrompt(action, content);

      const requestConfig = {
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
        },
        timeout: 30000,
      };

      const requestBody =
        action === "draft" || action === "generate"
          ? {
              user_prompt: content,
              category,
            }
          : {
              content: fallbackContent,
              category,
            };

      return axios.post(endpoint, requestBody, requestConfig).catch((xgenError) => {
        // Dedicated endpoints may intermittently return 5xx.
        // Fallback to refine-with-ai while preserving action intent.
        const status = xgenError?.response?.status;
        const shouldFallbackToRefine =
          ["rewrite", "simplify", "grammar"].includes(action) &&
          (!status || status >= 500);

        if (!shouldFallbackToRefine) {
          throw xgenError;
        }

        return axios.post(
          `${XGEN_AUTHORING_BASE}/refine-with-ai?rows=10`,
          {
            content: composeInlinePrompt(action, content),
            category,
          },
          requestConfig
        );
      });
    });
}

export async function askAI(payload) {
  const response = await aiAxios.post(ASK_AI, payload, {
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
    },
    params: {
      rows: 5,
    },
    timeout: 30000,
  });

  return response.data;
}

export function controlledLanguageAuthoringContent(payload) {
  const rows = payload?.rows || 10;
  return authoringAxios.post(
    `${AI_CONTROLLED_LANGUAGE}?rows=${rows}`,
    {
      content: payload?.content || "",
      category: payload?.category || "Setup Instruction",
      approved_documents: Array.isArray(payload?.approved_documents)
        ? payload.approved_documents
        : [],
    },
    {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      timeout: 30000,
    }
  );
}